import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  Text,
  Badge,
  Banner,
  ErrorMessage,
  InfoBox,
  InfoRow,
  SimpleModal,
  Box,
  Flex,
  Grid,
  Link
} from '@sequencehq/core-components'
import {
  GreyGrey100,
  IndigoIndigo60,
  Primary13Regular,
  Primary14ExtendedSemiBold
} from '@sequencehq/core-components'
import { usePersistMagicTableQueryParams } from '@sequencehq/core-components'
import { usePermissions } from 'lib/rbac'
import { useNavigate, useParams } from 'react-router-dom'
import invariant from 'tiny-invariant'
import { UsageMetricPreview } from './UsageMetricPreview'
import { usageMetricsPaths } from 'UsageMetrics/usageMetrics.router'

const metricPropertyMatchers = {
  is: 'matches',
  isNot: 'does not match'
}

export const ViewUsageMetric = () => {
  const navigate = useNavigate()
  const { persistMagicTableQueryParams } = usePersistMagicTableQueryParams()
  const { metricId } = useParams<{ metricId: string }>()

  invariant(metricId, 'metricId is required')

  const usageMetricQuery = useQuery(dashboard20240730Client.getUsageMetric, {
    id: metricId
  })
  const { can } = usePermissions()

  const handleClose = () => {
    navigate(persistMagicTableQueryParams('/usage-metrics'))
  }

  const usageMetric = usageMetricQuery.data
  const canEditMetric =
    can('org.usage:update') && usageMetric?.aggregationType !== 'CUSTOM'

  return (
    <SimpleModal
      isOpen={true}
      variant="drawer"
      title={usageMetric?.name || 'Usage Metric'}
      submitButtonText="Edit"
      cancelButtonText="Close"
      onClose={handleClose}
      onSubmit={() => {
        navigate(persistMagicTableQueryParams(usageMetricsPaths.edit(metricId)))
        return true
      }}
      showSubmitButton={false}
      isLoading={usageMetricQuery.isPending}
      data-testid="viewUsageMetric.drawer"
      closeOnUpdate={false}
    >
      {!usageMetric ? (
        <Flex alignItems="center" justifyContent="center" height="100%">
          <ErrorMessage />
        </Flex>
      ) : (
        <Flex direction="column" gap="20px">
          {usageMetric.aggregationType === 'CUSTOM' && (
            <Banner variant="info">
              Editing is not available for custom metrics. Please reach out to
              the Sequence team for any changes.
            </Banner>
          )}

          <Box>
            <Grid
              templateColumns="1fr auto"
              alignItems="center"
              columnGap="8px"
              marginBottom="8px"
            >
              <Box {...Primary14ExtendedSemiBold}>Details</Box>
              {canEditMetric && (
                <Link.Unstyled
                  data-testid="viewUsageMetric.editLink"
                  to={persistMagicTableQueryParams(
                    `/usage-metrics/${usageMetric.id}/edit`
                  )}
                  {...Primary13Regular}
                  style={{
                    color: IndigoIndigo60
                  }}
                >
                  Edit
                </Link.Unstyled>
              )}
            </Grid>
            <InfoBox data-testid="usageMetric.info">
              <InfoRow label="Metric ID">{usageMetric.id}</InfoRow>
              <InfoRow label="Description">{usageMetric.description}</InfoRow>
              {usageMetric.aggregationType !== 'CUSTOM' && (
                <InfoRow label="Event type">
                  <Badge sentiment="monospaced" textTransform="none">
                    {usageMetric.eventType}
                  </Badge>
                </InfoRow>
              )}
              <InfoRow label="Calculation">
                {usageMetric.aggregationType === 'CUSTOM'
                  ? 'Custom calculation'
                  : `${usageMetric.aggregationType} of usage values`}
              </InfoRow>

              {usageMetric.aggregationProperty ? (
                <InfoRow label="Over event property">
                  <Badge sentiment="monospaced" textTransform="none">
                    {usageMetric.aggregationProperty}
                  </Badge>
                </InfoRow>
              ) : null}

              {usageMetric.propertyFilters
                ? Object.entries(usageMetric.propertyFilters).map(
                    ([key, value], index) => (
                      <InfoRow
                        key={`${key}${value.toString()}`}
                        label={index === 0 ? 'Filters' : ''}
                      >
                        <Flex alignItems="center">
                          <Badge sentiment="monospaced" textTransform="none">
                            {key}
                          </Badge>
                          <Text
                            {...Primary13Regular}
                            color={GreyGrey100}
                            px="8px"
                          >
                            {usageMetric.propertiesToNegate &&
                            usageMetric.propertiesToNegate.includes(key)
                              ? metricPropertyMatchers['isNot']
                              : metricPropertyMatchers['is']}
                          </Text>
                          <Badge sentiment="monospaced" textTransform="none">
                            {value.toString()}
                          </Badge>
                        </Flex>
                      </InfoRow>
                    )
                  )
                : null}
            </InfoBox>
          </Box>

          <UsageMetricPreview
            usageMetricId={metricId}
            parameters={usageMetric.parameters}
            aggregationType={usageMetric.aggregationType}
          />
        </Flex>
      )}
    </SimpleModal>
  )
}
